import AutocompleteTag, { TagProps } from 'components/AutocompleteTag';
import { ComboboxChip } from 'components/ui/combobox';
import { ReactNode } from 'react';
import { PartyNameParty } from 'utils/partyNameUtil';
import { OpponentChip } from './OpponentChip';
import PersonaNonGrataChip from './PersonaNonGrataChip';

/**
 * Default `renderTags` implementation for contact multi-select dropdowns. Each selected contact is
 * shown as a tag with the contact's name plus an inline `<PersonaNonGrataChip>` when the contact is
 * flagged persona non grata and an `<OpponentChip>` when it opposes a case — mirrors the
 * dropdown-option adornments so selected values carry the same signal.
 */
export function renderContactTags<TOption extends PartyNameParty>(
    getLabel: (option: TOption) => string | null,
    value: TOption[],
    getTagProps: (params: { index: number }) => object
): ReactNode {
    return value.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index }) as TagProps;
        const label = getLabel(option) ?? '';
        const tag = (
            <AutocompleteTag
                key={key ?? index}
                {...tagProps}
                label={label}
                title={label || undefined}
                adornment={
                    <>
                        {option.isPersonaNonGrata && <PersonaNonGrataChip />}
                        <OpponentChip state={option.opponentCaseState} />
                    </>
                }
            />
        );
        // A record section's tag is a chip the input's arrow keys walk, as the multi-select's own chips are:
        // Backspace on it removes that contact, not only the last one, and its remove button shows meanwhile.
        return tagProps.section ? (
            <ComboboxChip key={key ?? index} showRemove={false} className="h-auto max-w-full bg-transparent p-0">
                {tag}
            </ComboboxChip>
        ) : (
            tag
        );
    });
}
